import "./Results.css";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Card, { CardText } from "../../../commons/Card/Card";
import ImageContainer from "../../../commons/ImageContainer/ImageContainer";
import { capitalize } from "../../../../utils";
import clients from "../../../../assets/needs/clients.png";
import long_term from "../../../../assets/needs/long_term.png";
import short_term from "../../../../assets/needs/short_term.png";
import stock from "../../../../assets/needs/stock.png";
import vendors from "../../../../assets/needs/vendors.png";
import {
  SideDrawer,
  SideDrawerHeader,
} from "../../../commons/SideDrawer/SideDrawer";
import usePartners from "../../usePartners";
import Pill from "../../Common/Pill/Pill";
import Typo from "../../../commons/Typo/Typo";

export default function Results({
  onboarding = false,
  results,
  children,
  onClick,
}) {
  delete results.long_term; // temp. - waiting to have a use-case in such need

  const { t } = useTranslation(["common", "financing"]);
  const [selectedResult, setSelectedResult] = useState(null);
  const [drawer, setDrawer] = useState(false);
  const toggleDrawer = () => setDrawer((drawer) => !drawer);

  useEffect(() => {
    if (!drawer) {
      setSelectedResult("");
    }
  }, [drawer]);

  const imagesMap = {
    clients,
    long_term,
    short_term,
    stock,
    vendors,
  };

  return (
    <div className="Results">
      <div className="Results-container">
        {Object.entries(results)
          .sort(([result]) =>
            Object.values(result).some((value) => value) ? -1 : 1,
          ) // sort by eligible first
          .map(([id, result]) => (
            <Result
              key={id}
              id={id}
              name={t(`financing:assessment.results.keys.${id}.name`)}
              img={imagesMap[id]}
              description={t(
                `financing:assessment.results.keys.${id}.description`,
              )}
              eligible={result && Object.values(result).some((value) => value)}
              onClick={() => onClick(id)}
            />
          ))}
      </div>
      <PartnersDrawer
        result={selectedResult && Object.entries(results[selectedResult])}
        open={drawer}
        onClose={toggleDrawer}
        onboarding={onboarding}
      >
        {children}
      </PartnersDrawer>
    </div>
  );
}

function Result({
  id = "stock",
  name = "Dépenses fournisseurs (stock)",
  description,
  img,
  eligible = false,
  onClick,
}) {
  const { t } = useTranslation(["common"]);

  return (
    <Card className="Result" title={name} onClick={onClick}>
      <ImageContainer
        className="Result-img"
        src={img}
        alt={name}
        backgroundColor={`var(--color-need-${id})`}
      />
      <CardText heading={name} subheading={description} />
      <div style={{ position: "absolute", top: "1rem", right: "1rem" }}>
        <Pill
          text={
            eligible
              ? capitalize(t("common:available"))
              : capitalize(t("common:unavailable"))
          }
          color={eligible ? "green" : "red"}
        />
      </div>
    </Card>
  );
}

export function PartnersDrawer({
  onboarding = false,
  result = [
    ["partnerA", true],
    ["partnerB", false],
    ["partnerC", true],
  ],
  open,
  onClose,
  children,
}) {
  const { t } = useTranslation(["common", "financing"]);
  const [selectedPartner, setSelectedPartner] = useState("");

  // expand first partner in the list on init
  const openFirstPartner = false;
  useEffect(() => {
    if (openFirstPartner) {
      if (result) setSelectedPartner(result[0][0]);
    }
  }, [result]);

  const handleSelectPartner = (newPartner) => {
    setSelectedPartner((prevPartner) =>
      prevPartner === newPartner ? "" : newPartner,
    );
  };

  const partnerTransKey = (partner) =>
    `financing:assessment.partners.${partner}`;

  const getSection = (partner, section) => {
    return {
      id: section,
      title: t([partnerTransKey(partner), section, "title"].join(".")),
      items: t([partnerTransKey(partner), section, "items"].join("."), {
        returnObjects: true,
      }),
    };
  };

  const partnersData = usePartners();

  const partners = partnersData
    .map((partner) => partner.id)
    .map((partnerID) => ({
      id: partnerID,
      name: partnersData.filter((partner) => partner.id === partnerID)[0].name,
      summary: t([partnerTransKey(partnerID), "summary"].join(".")),
      logo: {
        src: partnersData.filter((partner) => partner.id === partnerID)[0].logo,
        alt: `logo-${partnerID}`,
      },
      sections: ["about", "conditions", "costs"].map((section) =>
        getSection(partnerID, section),
      ),
    }));

  const handleCloseDrawer = () => {
    setSelectedPartner("");
    onClose();
  };

  return (
    <SideDrawer anchor={"right"} open={open} onClose={handleCloseDrawer}>
      <SideDrawerHeader
        title={t("financing:assessment.drawer.title")}
        subtitle={t("financing:assessment.drawer.message")}
        onClose={handleCloseDrawer}
      />

      <div className="PartnersDrawer-content">
        <div className="PartnersDrawer-content-partners">
          {result &&
            result
              .sort((resultA, resultB) => (resultA[0] < resultB[0] ? -1 : 1)) // sort partner ids alphabetically
              .map(([partnerID, eligible]) => (
                <Partner
                  onboarding={onboarding}
                  key={partnerID}
                  eligible={eligible}
                  selected={selectedPartner === partnerID}
                  onSelect={handleSelectPartner}
                  {...partners.filter(
                    (partner) =>
                      partner.id.toLowerCase() === partnerID.toLowerCase(),
                  )[0]}
                />
              ))}
        </div>
        {children}
      </div>
    </SideDrawer>
  );
}

function Partner({
  onboarding = false,
  selected,
  onSelect,
  eligible,
  summary,
  id,
  name,
  logo,
  sections,
}) {
  const { t } = useTranslation(["common", "financing"]);

  return (
    <div className="Partner">
      <div
        className={["Partner-wrapper", onboarding ? "onboarding" : ""]
          .filter(Boolean)
          .join(" ")}
        onClick={!onboarding ? () => onSelect(id) : undefined}
        style={{
          borderBottom: !selected
            ? "1px solid var(--color-neutral-2)"
            : "1px dashed var(--color-neutral-2)",
        }}
      >
        <div className="Partner-wrapper-header">
          <span className="Partner-wrapper-header-name">{name}</span>
          <Pill
            text={
              eligible
                ? t("financing:assessment.partners.common.eligible")
                : t("financing:assessment.partners.common.nonEligible")
            }
            color={eligible ? "green" : "red"}
          />
        </div>
      </div>

      <PartnerDetails
        key={id}
        eligible={eligible}
        selected={selected}
        id={id}
        logo={logo}
        summary={summary}
        sections={sections}
      />
    </div>
  );
}

function PartnerDetails({ selected, logo, summary, sections }) {
  return (
    <div
      className="Partner-details"
      style={{
        display: selected ? "block" : "none",
        borderBottom: selected ? "1px solid var(--color-neutral-2)" : "none",
      }}
    >
      <div style={{ display: "none" }}>
        <ImageContainer
          className="Partner-details-logo"
          alt={logo.alt}
          src={logo.src}
        />
      </div>

      <Typo className="Partner-details-summary">{summary}</Typo>

      <div style={{ marginTop: "1rem" }}>
        {sections &&
          sections.map((section) => (
            <PartnerSection key={section.title} {...section} />
          ))}
      </div>
    </div>
  );
}

function PartnerSection({ title, items }) {
  return (
    <details>
      <summary style={{ margin: "0.5rem 0", cursor: "pointer" }}>
        {title}
      </summary>
      <ul>
        {Array.isArray(items) &&
          items.map((item, index) => <li key={index}>{item}</li>)}
      </ul>
    </details>
  );
}
